/**
 * E10. Ломбард и микрозайм: «Занять» в конверте при долге или после
 * проигрыша в азартной игре (`flags.lastLoss`), с 18 лет, один заём за раз.
 * Ломбард: +0,2 годового дохода, через год вернуть ×1,3; микрозайм:
 * +0,5 дохода, через год ×2,2. Возврат, невозврат (в `debt`) и коллекторы
 * (p 0,3 в год, счастье −5, отношения −3) — в движке (`serviceHoldings`).
 * Это не мини-игра, а дверь, через которую азарт становится долгом;
 * «пропустить» — не занимать.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { FLAG_LAST_LOSS, FLAG_LOAN_DUE, FLAG_LOAN_KIND, type Life, type MinigameOutcome } from '@life/engine';
import type { ExpectancyText, MinigameDef } from '../types';
import { noPlay, yearlyIncome } from '../casino/common';
import { ADULT_AGE, heldOf } from './common';

export const LOAN_PAWN_SHARE = 0.2;
export const LOAN_PAWN_RATE = 1.3;
export const LOAN_MICRO_SHARE = 0.5;
export const LOAN_MICRO_RATE = 2.2;

export type LoanKind = 'pawn' | 'microloan';

export interface LoansState {
  pawnAmount: number;
  pawnDue: number;
  microAmount: number;
  microDue: number;
}

export interface LoansInput {
  kind: LoanKind;
}

export const loans: MinigameDef<LoansInput, LoansState> = {
  id: 'loans',
  section: 'E',
  triggers: [{ kind: 'envelope' }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    if (life.age < ADULT_AGE) return false;
    if (heldOf(life, FLAG_LOAN_DUE) > 0) return false;
    return life.debt > 0 || life.flags[FLAG_LAST_LOSS] === true;
  },
  setup(life: Life, ds: Dataset): LoansState {
    const income = Math.max(0, yearlyIncome(ds, life));
    const pawnAmount = income * LOAN_PAWN_SHARE;
    const microAmount = income * LOAN_MICRO_SHARE;
    return { pawnAmount, pawnDue: pawnAmount * LOAN_PAWN_RATE, microAmount, microDue: microAmount * LOAN_MICRO_RATE };
  },
  play(state: LoansState, input: LoansInput, life: Life): MinigameOutcome {
    if (heldOf(life, FLAG_LOAN_DUE) > 0) return noPlay();
    const micro = input.kind === 'microloan';
    const amount = micro ? state.microAmount : state.pawnAmount;
    const due = micro ? state.microDue : state.pawnDue;
    if (!(amount > 0)) return noPlay();
    return {
      moneyDelta: amount,
      urgeDelta: 0,
      flags: { [FLAG_LOAN_DUE]: due, [FLAG_LOAN_KIND]: micro ? 'microloan' : 'pawn' },
      logKey: micro ? 'minigame.loans.microloan' : 'minigame.loans.pawn',
      logParams: { amount: Math.round(amount), due: Math.round(due) },
    };
  },
  expected(): MinigameOutcome {
    return noPlay();
  },
  expectancyText: (): ExpectancyText => ({
    key: 'minigame.expect.loans',
    params: { pawn: Math.round((LOAN_PAWN_RATE - 1) * 100), micro: Math.round((LOAN_MICRO_RATE - 1) * 100) },
  }),
};
